import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UserLoginData } from "../../components/common/charts/Enums";
import close from "../../assets/close.svg";
import {
  MaintenanceBannerService,
  MaintenanceBannerVM,
} from "../../services/maintenanceBanner.service";
import apiHelper from "../../utils/apiHelper";
import { MyContext } from "../../utils/context/LoginContext";
import H3 from "../typography/H3";
import "./maintenance-banner-overlay.css";

const DISMISSED_BANNER_KEY = "dismissedMaintenanceBannerId";

// Login.tsx dispatches this the instant a login completes (session storage + OpenAPI.TOKEN
// already written), so the overlay can fetch deterministically instead of relying on a
// same-tick context/render race with App.tsx's own isLogin/loginToken derivation. Without this,
// the banner only reliably appeared after an unrelated re-render (e.g. a page refresh) forced
// this component's effect to recompute `loginToken` from sessionStorage with the new value.
export const MAINTENANCE_BANNER_LOGIN_EVENT = "maintenance-banner:login";

const MaintenanceBannerOverlay = () => {
  const { isLogin } = (useContext(MyContext) ?? {}) as { isLogin?: boolean };
  const [banner, setBanner] = useState<MaintenanceBannerVM | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const fetchedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchActiveBanner = async (token: string) => {
      if (fetchedTokenRef.current === token) {
        return;
      }

      fetchedTokenRef.current = token;

      const response = await apiHelper(
        MaintenanceBannerService.getApiMaintenanceBannerActive,
        { showNotification: false },
      );

      const activeBanner = response?.data?.success ?? null;
      const dismissedBannerId = sessionStorage.getItem(DISMISSED_BANNER_KEY);

      setBanner(activeBanner);
      setIsVisible(
        Boolean(
          activeBanner &&
            String(activeBanner.id) !== dismissedBannerId,
        ),
      );
    };

    const loginToken = sessionStorage.getItem(UserLoginData.token);

    if (!isLogin || !loginToken) {
      fetchedTokenRef.current = null;
      setBanner(null);
      setIsVisible(false);
      return;
    }

    fetchActiveBanner(loginToken);

    // Fires synchronously the moment Login.tsx finishes writing sessionStorage/OpenAPI.TOKEN,
    // independent of whether this component's own render happened to catch the new token yet.
    const handleLoginEvent = () => {
      const currentToken = sessionStorage.getItem(UserLoginData.token);
      if (currentToken) {
        fetchActiveBanner(currentToken);
      }
    };

    window.addEventListener(MAINTENANCE_BANNER_LOGIN_EVENT, handleLoginEvent);
    return () => {
      window.removeEventListener(MAINTENANCE_BANNER_LOGIN_EVENT, handleLoginEvent);
    };
  }, [isLogin]);

  const handleClose = () => {
    if (!banner?.id) {
      setIsVisible(false);
      return;
    }

    sessionStorage.setItem(DISMISSED_BANNER_KEY, String(banner.id));
    setIsVisible(false);
  };

  if (typeof document === "undefined" || !banner || !isVisible) {
    return null;
  }

  return createPortal(
    <div className="maintenance-banner-overlay">
      <div className="maintenance-banner-card">
        <div className="maintenance-banner-header">
          <H3 text={banner.title || "Maintenance Banner"} />
          <button
            type="button"
            className="maintenance-banner-close"
            onClick={handleClose}
            aria-label="Close maintenance banner"
          >
            <img src={close} alt="Close maintenance banner" />
          </button>
        </div>

        <div
          className="maintenance-banner-body"
          dangerouslySetInnerHTML={{ __html: banner.messageBody || "" }}
        />
      </div>
    </div>,
    document.body,
  );
};

export default MaintenanceBannerOverlay;
